import { useCallback, useEffect, useRef } from 'react';

/** What PrimeReact's Dropdown, MultiSelect and Calendar all expose on their ref. */
export interface DialogOverlayHandle {
  hide(): void;
  getElement(): HTMLElement | null;
}

export interface DialogOverlayGuard {
  /** The control's `onShow`: starts watching the dialog body that holds the control. */
  watch: (handle: DialogOverlayHandle | null) => void;
  /** The control's `onHide`: stops watching. */
  release: () => void;
}

/**
 * Closes a field's open list once its field scrolls out of the dialog body.
 *
 * A select, multi-select or calendar panel is portalled to `<body>` and stacked above the dialog, so the
 * dialog's scrolling body can neither clip it nor paint over it -- which is what keeps a list near the foot of
 * a long form usable. The cost is that on a scroll PrimeReact only re-aligns the panel to its field: when the
 * field leaves the body's visible area, under the header or the footer, the panel follows it there and is
 * drawn over the header, the footer and the body's scrollbar, pointing at a field that can no longer be seen.
 *
 * So the panel stays open, and anchored, for as long as its field is visible in the body, and closes the
 * moment any part of the field scrolls out of it. Outside a dialog there is nothing to watch, and the control
 * keeps PrimeReact's behaviour unchanged.
 */
export function useDialogOverlayGuard(): DialogOverlayGuard {
  const stopRef = useRef<(() => void) | null>(null);

  const release = useCallback(() => {
    stopRef.current?.();
    stopRef.current = null;
  }, []);

  const watch = useCallback(
    (handle: DialogOverlayHandle | null) => {
      release();

      const trigger = handle?.getElement() ?? null;
      const body = trigger?.closest('.p-dialog-content') ?? null;
      if (handle === null || trigger === null || !(body instanceof HTMLElement)) return;

      const onScroll = () => {
        const field = trigger.getBoundingClientRect();
        const visible = body.getBoundingClientRect();
        if (field.top < visible.top || field.bottom > visible.bottom) handle.hide();
      };

      body.addEventListener('scroll', onScroll, { passive: true });
      stopRef.current = () => {
        body.removeEventListener('scroll', onScroll);
      };
    },
    [release],
  );

  /* A dialog closed with a list still open unmounts the control without an `onHide`. */
  useEffect(() => release, [release]);

  return { watch, release };
}
